import { useCallback, useEffect, useState } from 'react'
import { Control, FieldErrors, useFieldArray, useWatch } from 'react-hook-form'
import { CatalogueProductOption } from 'types/bundleProduct'
import ProductSearchInput from './productSearchInput'
import BundleItemCard from './bundleItemCard'

type BundleItemsFormShape = {
  bundleItems: { catalogue: string }[]
}

type Props = {
  control: Control<any>
  register: any
  errors: FieldErrors<BundleItemsFormShape>
  initialProducts?: Record<string, CatalogueProductOption>
}

/**
 * Reusable "child products" section for a bundle product: a catalogue
 * search box that adds rows, plus the list of BundleItemCard rows below it.
 * Used by both the Create Product form and the Master Data update form,
 * since both need the identical search/add/list behavior.
 */
const BundleItemsFields = ({
  control,
  register,
  errors,
  initialProducts,
}: Props) => {
  const { fields, append, replace } = useFieldArray({
    control,
    name: 'bundleItems',
  })

  const bundleItems = useWatch({ control, name: 'bundleItems' }) || []

  // Display-only cache of catalogue product info, keyed by catalogue id.
  // Uses a Map rather than a plain object so barcode/id keys are never
  // treated as numeric property keys (which would reorder them or drop a
  // leading zero, e.g. a barcode like "0123456"). Seeded once from
  // initialProducts (the catalogue's embedded bundleItems on an existing
  // bundle) and topped up whenever the user picks a new product from
  // search. Entries are never pruned on remove — a leftover entry for a
  // product no longer in bundleItems is simply never looked up again, so
  // there's nothing to keep in sync. This is purely a lookup cache for
  // rendering; it never touches form state or the submitted payload.
  const [productsCache, setProductsCache] = useState<
    Map<string, CatalogueProductOption>
  >(() => new Map(Object.entries(initialProducts || {})))

  useEffect(() => {
    if (initialProducts) {
      setProductsCache((prev) => {
        const next = new Map(prev)
        Object.entries(initialProducts).forEach(([id, product]) => {
          next.set(id, product)
        })
        return next
      })
    }
  }, [initialProducts])

  const handleAddProduct = useCallback(
    (option: CatalogueProductOption) => {
      setProductsCache((prev) => new Map(prev).set(option._id, option))
      append({
        catalogue: option._id,
        price: null,
        specialPrice: null,
        quantity: 1,
      })
    },
    [append],
  )

  // Use replace() rather than remove() when dropping a row: remove() shifts
  // values across the *existing* uncontrolled price/specialPrice inputs by
  // position instead of fully remounting them, so a stale value can resurface
  // on a later row added at the same index. replace() swaps the whole array
  // in one go, forcing every row to remount cleanly.
  const handleRemoveProduct = useCallback(
    (index: number) => {
      replace(bundleItems.filter((_: unknown, i: number) => i !== index))
    },
    [bundleItems, replace],
  )

  const excludeIds = bundleItems
    .map((item: { catalogue: string }) => item.catalogue)
    .filter(Boolean)

  const hasArrayLevelError =
    Boolean(errors.bundleItems?.root?.message) ||
    typeof errors.bundleItems?.message === 'string'

  return (
    <div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <ProductSearchInput
          excludeIds={excludeIds}
          onSelect={handleAddProduct}
        />

        {fields.length > 0 && (
          <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-500">
            <span className="font-mono tabular-nums text-primary">
              {fields.length}
            </span>
            {fields.length === 1 ? 'product' : 'products'}
          </span>
        )}
      </div>

      <div className="mt-4">
        {fields.length === 0 ? (
          <div
            className={`rounded-2xl border border-dashed px-6 py-10 text-center transition-colors ${
              hasArrayLevelError
                ? 'border-error/30 bg-error/[0.03]'
                : 'border-gray-200 bg-gray-50/50'
            }`}
          >
            <p className="text-sm font-medium text-gray-500">
              This bundle has no components yet
            </p>
            <p className="mt-1 text-xs text-gray-400">
              Search the catalogue above and add at least two products to build
              it.
            </p>
          </div>
        ) : (
          <div
            className={`rounded-2xl border bg-white shadow-sm ${
              hasArrayLevelError ? 'border-error/40' : 'border-gray-100'
            }`}
          >
            {fields.map((field, index) => (
              <div
                key={field.id}
                className={index > 0 ? 'border-t border-gray-100' : ''}
              >
                <BundleItemCard
                  index={index}
                  product={productsCache.get(bundleItems[index]?.catalogue)}
                  register={register}
                  errors={errors}
                  onRemove={() => handleRemoveProduct(index)}
                  defaultExpanded={!bundleItems[index]?.price}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {errors.bundleItems?.root?.message && (
        <p className="mt-2 text-xs font-medium text-error">
          {errors.bundleItems.root.message}
        </p>
      )}
      {typeof errors.bundleItems?.message === 'string' && (
        <p className="mt-2 text-xs font-medium text-error">
          {errors.bundleItems.message}
        </p>
      )}
    </div>
  )
}

export default BundleItemsFields
